import {
  BadRequestException,
  Body,
  Controller,
  Delete,
  Get,
  Param,
  Post,
  Req,
  UploadedFile,
  UseGuards,
  UseInterceptors,
} from '@nestjs/common';
import { FileInterceptor } from '@nestjs/platform-express';
import { Throttle } from '@nestjs/throttler';
import { ApiBearerAuth, ApiOperation, ApiSecurity, ApiTags } from '@nestjs/swagger';
import { AdminAccessGuard } from '../auth/admin-access.guard';
import { AuthGuard, GuardedRequest } from '../auth/auth.guard';
import { DemoModeGuard } from '../auth/demo-mode.guard';
import { StoreContextService } from '../tenancy/store-context.service';
import { PrismaService } from '@cart/api-data-access';
import { MediaTrashService } from './media-trash.service';
import { canUseByos, resolvePlanTier } from '../commerce-admin/plan-quota.constants';
import { MediaAssetService } from './media-asset.service';
import { MediaBackupService } from './media-backup.service';
import { MediaMigrationService } from './media-migration.service';
import { MediaUploadService, PROXIED_UPLOAD_MAX_BYTES } from './media-upload.service';
import { MEDIA_OWNER_TYPES, MediaOwnerType } from './media-key.util';
import { ALLOCATION_MODES, AllocationMode } from './storage-allocation.util';
import { StorageConfigService } from './storage-config.service';
import { StorageQuotaService } from './storage-quota.service';

/**
 * Merchant-facing storage and media API.
 *
 * Everything here is scoped to the caller's organization, taken from the
 * session — never from the request body. Nothing on this controller returns a
 * credential in any form; the wizard sends them in and gets back a masked key
 * id and a connection verdict.
 */
@ApiTags('Admin · Media')
@ApiBearerAuth()
@ApiSecurity('store-id')
@Controller('admin/media')
@UseGuards(AuthGuard, AdminAccessGuard, DemoModeGuard)
export class MediaController {
  constructor(
    private readonly assets: MediaAssetService,
    private readonly uploads: MediaUploadService,
    private readonly quota: StorageQuotaService,
    private readonly configs: StorageConfigService,
    private readonly migrations: MediaMigrationService,
    private readonly backups: MediaBackupService,
    private readonly storeContext: StoreContextService,
    /** Only for the plan lookup on the overview; everything else goes via a service. */
    private readonly prisma: PrismaService,
    private readonly trashService: MediaTrashService
  ) {}

  private orgId(): string {
    return this.storeContext.organizationId();
  }

  private actor(req: GuardedRequest): string {
    return req.user?.id ?? req.user?.email ?? 'system';
  }

  // --- Dashboard ------------------------------------------------------------

  /**
   * Everything the storage page renders in one call — usage, the per-backend
   * breakdown, and where each backend stands. The breakdown is what makes
   * switching legible to a merchant: "100 files here, 200 there, all live".
   */
  @Get('storage')
  @ApiOperation({ summary: 'Storage overview: usage, backends, breakdown' })
  async storageOverview() {
    const organizationId = this.orgId();
    const [usage, backends, breakdown, trashedBytes, org] = await Promise.all([
      this.quota.getUsage(organizationId),
      this.configs.listForOrg(organizationId),
      this.assets.breakdownByOwnerType(organizationId),
      this.assets.trashedBytes(organizationId),
      this.prisma.organization.findUnique({
        where: { id: organizationId },
        select: { plan: true, byosEnabled: true },
      }),
    ]);

    return {
      usage,
      backends,
      breakdown,
      trashedBytes,
      // Sent so the page can present "your own bucket" as a locked premium
      // feature rather than offering it and failing at connect time. The API
      // enforces the same rule regardless — this only decides what is drawn.
      canUseByos: org ? canUseByos(org.plan, org.byosEnabled) : false,
      planTier: org ? resolvePlanTier(org.plan) : 'free',
      activeBackend: backends.find(b => b.isWriteTarget) ?? null,
      // Retained backends holding assets are the ones a merchant must not
      // disconnect without dealing with the files first.
      retainedWithAssets: backends.filter(b => !b.isWriteTarget && b.assetCount > 0),
    };
  }

  @Get('usage')
  usage() {
    return this.quota.getUsage(this.orgId());
  }

  // --- Trash ----------------------------------------------------------------

  /** What is recoverable, how much space it holds, and for how much longer. */
  @Get('trash')
  @ApiOperation({ summary: 'Trash summary: file count, bytes held, retention window' })
  trashSummary() {
    return this.trashService.summary(this.orgId());
  }

  /**
   * Purge everything in the trash now.
   *
   * Irreversible, and the only way for a merchant at their quota to reclaim
   * space without waiting out the retention window. The confirmation that
   * names what is lost belongs in the UI; this is the action behind it.
   */
  @Delete('trash')
  @ApiOperation({ summary: 'Empty trash — permanently deletes every trashed file' })
  emptyTrash() {
    return this.trashService.empty(this.orgId());
  }

  // --- Store allocation -----------------------------------------------------

  /**
   * How the merchant has divided their one pool between their stores.
   *
   * This lives on the merchant side only. The platform sells storage to the
   * account; splitting it is the merchant's call, so there is no equivalent
   * platform-admin route.
   */
  @Get('storage/allocation')
  @ApiOperation({ summary: 'Per-store split of the account storage pool' })
  allocation() {
    return this.quota.getAllocation(this.orgId());
  }

  @Post('storage/allocation')
  @ApiOperation({ summary: 'Set how storage is shared between stores' })
  setAllocation(@Body() body: Record<string, unknown>, @Req() req: GuardedRequest) {
    const mode = String(body['mode'] ?? '');
    if (!ALLOCATION_MODES.includes(mode as AllocationMode)) {
      throw new BadRequestException(`mode must be one of: ${ALLOCATION_MODES.join(', ')}`);
    }

    const raw = Array.isArray(body['stores']) ? (body['stores'] as Record<string, unknown>[]) : [];
    const allocations = raw.map(entry => ({
      storeId: String(entry['storeId'] ?? ''),
      quotaBytes: entry['quotaBytes'] == null ? null : Number(entry['quotaBytes']),
      quotaPercent: entry['quotaPercent'] == null ? null : Number(entry['quotaPercent']),
    }));

    return this.quota.setAllocation(this.orgId(), mode as AllocationMode, allocations, this.actor(req));
  }

  // --- Backends -------------------------------------------------------------

  @Post('storage/use-platform')
  @ApiOperation({ summary: 'Switch new uploads to platform-managed storage' })
  usePlatform(@Req() req: GuardedRequest) {
    // Existing media stays exactly where it is — this only moves the write
    // target (plan §5).
    return this.configs.usePlatformStorage(this.orgId(), this.actor(req));
  }

  @Post('storage/byos')
  @ApiOperation({ summary: 'Connect your own R2/S3 bucket and switch new uploads to it' })
  async connectByos(@Body() body: Record<string, unknown>, @Req() req: GuardedRequest) {
    const provider = String(body['provider'] ?? '').toLowerCase();
    if (provider !== 'r2' && provider !== 's3') {
      throw new BadRequestException('provider must be "r2" or "s3"');
    }
    const required = ['bucket', 'publicBaseUrl', 'accessKeyId', 'secretAccessKey'] as const;
    for (const field of required) {
      if (!body[field]) throw new BadRequestException(`${field} is required`);
    }

    return this.configs.connectByos(
      this.orgId(),
      {
        provider,
        bucket: String(body['bucket']),
        region: body['region'] ? String(body['region']) : undefined,
        endpoint: body['endpoint'] ? String(body['endpoint']) : undefined,
        publicBaseUrl: String(body['publicBaseUrl']),
        pathPrefix: body['pathPrefix'] ? String(body['pathPrefix']) : undefined,
        accessKeyId: String(body['accessKeyId']),
        secretAccessKey: String(body['secretAccessKey']),
      },
      this.actor(req)
    );
  }

  @Post('storage/:configId/activate')
  @ApiOperation({ summary: 'Make an existing backend the write target' })
  async activate(@Param('configId') configId: string, @Req() req: GuardedRequest) {
    await this.configs.activate(this.orgId(), configId, this.actor(req));
    return { success: true };
  }

  @Post('storage/:configId/rotate')
  @ApiOperation({ summary: 'Replace the credentials on a backend' })
  rotate(
    @Param('configId') configId: string,
    @Body() body: Record<string, unknown>,
    @Req() req: GuardedRequest
  ) {
    if (!body['accessKeyId'] || !body['secretAccessKey']) {
      throw new BadRequestException('accessKeyId and secretAccessKey are required');
    }
    return this.configs.rotateCredentials(
      this.orgId(),
      configId,
      {
        accessKeyId: String(body['accessKeyId']),
        secretAccessKey: String(body['secretAccessKey']),
      },
      this.actor(req)
    );
  }

  @Post('storage/:configId/verify')
  verify(@Param('configId') configId: string) {
    return this.configs.verify(configId);
  }

  @Delete('storage/:configId')
  @ApiOperation({ summary: 'Disconnect a retained backend (only once it holds no files)' })
  async disconnect(@Param('configId') configId: string, @Req() req: GuardedRequest) {
    await this.configs.disconnect(this.orgId(), configId, this.actor(req));
    return { success: true };
  }

  // --- Uploads --------------------------------------------------------------

  @Post('upload')
  @ApiOperation({ summary: 'Proxied upload (images up to 10 MB)' })
  @UseInterceptors(FileInterceptor('file', { limits: { fileSize: PROXIED_UPLOAD_MAX_BYTES } }))
  async upload(
    @UploadedFile() file: Express.Multer.File,
    @Body() body: Record<string, unknown>,
    @Req() req: GuardedRequest
  ) {
    if (!file) throw new BadRequestException('file is required');
    const ownerType = this.ownerType(body['ownerType']);

    return this.uploads.uploadProxied({
      organizationId: this.orgId(),
      storeId: this.storeContext.storeId(),
      ownerType,
      ownerId: body['ownerId'] ? String(body['ownerId']) : null,
      fileName: file.originalname,
      declaredMimeType: file.mimetype,
      buffer: file.buffer,
      compressionPercent: body['compressionPercent'] != null ? Number(body['compressionPercent']) : undefined,
      actorId: this.actor(req),
    });
  }

  /**
   * Presign is rate-limited well below the global throttle: it allocates
   * quota and mints a bearer URL on every call, which makes it a resource
   * endpoint rather than a read.
   */
  @Post('uploads/reserve')
  @Throttle({ default: { limit: 30, ttl: 60000 } })
  @ApiOperation({ summary: 'Reserve a direct upload (documents and files over 10 MB)' })
  reserve(@Body() body: Record<string, unknown>, @Req() req: GuardedRequest) {
    const ownerType = this.ownerType(body['ownerType']);
    if (!body['fileName'] || !body['mimeType'] || !body['sizeBytes']) {
      throw new BadRequestException('fileName, mimeType and sizeBytes are required');
    }

    return this.uploads.reserve({
      organizationId: this.orgId(),
      storeId: this.storeContext.storeId(),
      ownerType,
      ownerId: body['ownerId'] ? String(body['ownerId']) : null,
      fileName: String(body['fileName']),
      mimeType: String(body['mimeType']),
      sizeBytes: Number(body['sizeBytes']),
      actorId: this.actor(req),
    });
  }

  @Post('uploads/:assetId/confirm')
  confirm(@Param('assetId') assetId: string, @Req() req: GuardedRequest) {
    return this.uploads.confirm(assetId, this.orgId(), this.actor(req));
  }

  // --- Assets ---------------------------------------------------------------

  @Get('assets/:assetId/download-url')
  @ApiOperation({ summary: 'Short-lived signed URL for a private asset' })
  async downloadUrl(@Param('assetId') assetId: string) {
    const url = await this.assets.downloadUrlFor(assetId, this.orgId());
    return { url };
  }

  @Delete('assets/:assetId')
  async trash(@Param('assetId') assetId: string, @Req() req: GuardedRequest) {
    await this.assets.trash(assetId, this.orgId(), this.actor(req));
    return { success: true };
  }

  @Post('assets/:assetId/restore')
  async restoreFromTrash(@Param('assetId') assetId: string, @Req() req: GuardedRequest) {
    await this.assets.restoreFromTrash(assetId, this.orgId(), this.actor(req));
    return { success: true };
  }

  // --- Migration ------------------------------------------------------------

  @Post('storage/migrate')
  @ApiOperation({ summary: 'Copy media from one backend to another (optional consolidation)' })
  async migrate(@Body() body: Record<string, unknown>, @Req() req: GuardedRequest) {
    if (!body['sourceConfigId'] || !body['targetConfigId']) {
      throw new BadRequestException('sourceConfigId and targetConfigId are required');
    }
    const job = await this.migrations.createJob({
      organizationId: this.orgId(),
      sourceConfigId: String(body['sourceConfigId']),
      targetConfigId: String(body['targetConfigId']),
      kind: 'consolidation',
      actorId: this.actor(req),
    });
    // Kicked off in the background: a 10 GB copy is not an HTTP response.
    void this.migrations.runToCompletion(job.id);
    return job;
  }

  @Get('storage/migrations')
  listMigrations() {
    return this.migrations.listJobs(this.orgId());
  }

  @Get('storage/migrations/:jobId')
  migrationStatus(@Param('jobId') jobId: string) {
    return this.migrations.getJob(jobId, this.orgId());
  }

  // --- Backup ---------------------------------------------------------------

  @Get('backups')
  listBackups() {
    return this.backups.listRuns(this.orgId());
  }

  @Get('backups/:runId')
  backupRun(@Param('runId') runId: string) {
    return this.backups.getRun(runId, this.orgId());
  }

  @Post('backups/:assetId/restore')
  restoreFromBackup(@Param('assetId') assetId: string, @Req() req: GuardedRequest) {
    return this.backups.restoreAsset(assetId, this.orgId(), this.actor(req));
  }

  private ownerType(raw: unknown): MediaOwnerType {
    const value = String(raw ?? 'other');
    if (!MEDIA_OWNER_TYPES.includes(value as MediaOwnerType)) {
      throw new BadRequestException(`ownerType must be one of: ${MEDIA_OWNER_TYPES.join(', ')}`);
    }
    return value as MediaOwnerType;
  }
}
